import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Star, Trash2, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full rounded-lg border border-line bg-slate-100/70 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blaze";
const labelCls = "mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-500";

export const ReviewsManager = () => {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ author: "", rating: 5, text: "", time: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios.get(`${API}/site-reviews`).then(({ data }) => setReviews(data)).catch(() => {});
  };
  useEffect(load, []);

  const add = async () => {
    if (!form.author.trim() || !form.text.trim()) return toast.error("Reviewer name and review text are required.");
    setSaving(true);
    try {
      await axios.post(`${API}/site-reviews`, { ...form, rating: Number(form.rating) });
      toast.success("Review added — it now shows first on the website.");
      setForm({ author: "", rating: 5, text: "", time: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add review.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.author}'s review from the website?`)) return;
    try {
      await axios.delete(`${API}/site-reviews/${r.id}`);
      toast.success("Review removed.");
      load();
    } catch {
      toast.error("Could not remove review.");
    }
  };

  return (
    <div className="mt-10" data-testid="reviews-manager">
      <h2 className="font-display text-2xl font-semibold uppercase tracking-tight text-slate-900">
        Site <span className="text-blaze">Reviews</span>
      </h2>
      <p className="mt-1 text-sm text-slate-500">
        Copy your newest reviews from your Google listing and paste them here — they show first on the website (homepage cards + popup).
      </p>

      <div className="mt-5 rounded-2xl border border-line bg-white p-5" data-testid="add-review-form">
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className={labelCls}>Reviewer Name</label>
            <input className={inputCls} value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="e.g. Sarah Mitchell" data-testid="review-author-input" />
          </div>
          <div>
            <label className={labelCls}>Rating</label>
            <select className={inputCls} value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="review-rating-select">
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>When (label)</label>
            <input className={inputCls} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} placeholder='e.g. "2 days ago"' data-testid="review-time-input" />
          </div>
        </div>
        <div className="mt-4">
          <label className={labelCls}>Review Text</label>
          <textarea rows={3} className={inputCls} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Paste the customer's review here…" data-testid="review-text-input" />
        </div>
        <button
          onClick={add}
          disabled={saving}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blaze px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
          data-testid="add-review-btn"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add Review To Website
        </button>
      </div>

      {reviews.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="site-reviews-list">
          {reviews.map((r) => (
            <div key={r.id} className="flex items-start justify-between gap-3 rounded-2xl border border-line bg-white p-4" data-testid={`site-review-${r.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">
                  {r.author} <span className="ml-1 text-xs font-normal text-slate-500">{r.time}</span>
                </p>
                <span className="mt-1 flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                  ))}
                </span>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-600">{r.text}</p>
              </div>
              <button
                onClick={() => remove(r)}
                className="flex shrink-0 items-center gap-1.5 rounded-lg border border-red-300 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-red-600 hover:bg-red-50"
                data-testid={`remove-review-${r.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
